import React, { useState, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';

import { Form, Card, Row, Col } from "react-bootstrap";
import { MessageBar, MessageBarType, ActionButton, Spinner, SpinnerSize } from '@fluentui/react';

import { authGetClient } from "../../actions/auth-action";
import { useGetClients } from "../../hooks/useOfCreateUser";
import BulkUserService from "../../services/bulkuser.service";
import AuthVerify from "../../components/AuthVerify";

const FILE_MAX_SIZE_MB = 10;

//Carga masiva de usuarios: descarga de plantilla excel, carga y procesamiento.
//Si algo falla, el API devuelve el mismo excel con una hoja ERROR vinculada a las
//celdas con problemas (mismo patrón que BulkUpload.js de Web Core).
const BulkCreateUser = () => {
	const { t } = useTranslation();

	const { user: currentAuth } = useSelector((state) => state.auth);

	const userClientId = authGetClient();
	const [selectedClientId, setSelectedClientId] = useState(null);

	const [clientList, setClientList] = useState(null);
	useGetClients(clientList, setClientList, true);

	const fileInputRef = useRef(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [processedFile, setProcessedFile] = useState(null);
	const [uploadInProgress, setUploadInProgress] = useState(false);
	const [createdCount, setCreatedCount] = useState(null);

	const [messageConfig, setMessageConfig] = useState({
		message: null,
		messageType: MessageBarType.info,
		showMessage: false,
	});

	const showMessage = (show, messageType, message) => {
		setMessageConfig({ message: message, messageType: messageType, showMessage: show });
	};

	const getClientId = () => {
		const clientId = userClientId || selectedClientId;
		return (clientId && clientId !== "0" && clientId !== 0) ? clientId : null;
	};

	const downloadBase64File = (base64, fileName) => {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const getFileDate = () => {
		const now = new Date();
		return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
	};

	const onChangeClient = (e) => {
		setSelectedClientId(e.target.value);
		setSelectedFile(null);
		setProcessedFile(null);
		setCreatedCount(null);
	};

	const handlerExportTemplate = async () => {
		const clientId = getClientId();

		if (clientId === null) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-selectclient'));
			return;
		}

		setUploadInProgress(true);
		setProcessedFile(null);
		setCreatedCount(null);

		try {
			const serviceResponse = await BulkUserService.getBulkUsersTemplate(clientId);

			if (serviceResponse.data.succeeded === true && serviceResponse.data.data) {
				downloadBase64File(serviceResponse.data.data, `Plantilla Usuarios ${getFileDate()}.xlsx`);
				showMessage(true, MessageBarType.success, t('data-lang-msg-operationperformed'));
			}
			else {
				showMessage(true, MessageBarType.error, t('data-lang-msg-operationerror'));
			}
		}
		catch (error) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-operationerror'));
		}
		finally {
			setUploadInProgress(false);
		}
	};

	const handlerSelectFile = () => {
		fileInputRef.current.click();
	};

	const handleFileInput = (event) => {
		const file = event.target.files[0];

		if (file) {
			if (file.size > FILE_MAX_SIZE_MB * 1024 * 1024) {
				setSelectedFile(null);
				fileInputRef.current.value = '';
				showMessage(true, MessageBarType.warning, t('data-lang-msg-filesizeerror'));
			}
			else if (!file.name.endsWith('.xlsx')) {
				setSelectedFile(null);
				fileInputRef.current.value = '';
				showMessage(true, MessageBarType.error, t('data-lang-msg-documentoperationerror-1'));
			}
			else {
				setSelectedFile(file);
				setProcessedFile(null);
				setCreatedCount(null);
				showMessage(false, MessageBarType.warning, '');
			}
		}
	};

	const handlerUploadEmailFile = async () => {
		const clientId = getClientId();

		if (clientId === null) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-selectclient'));
			return;
		}

		if (selectedFile === null) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-selectfile'));
			return;
		}

		setUploadInProgress(true);
		setProcessedFile(null);
		setCreatedCount(null);

		try {
			const serviceResponse = await BulkUserService.postBulkUsersEmail(clientId, selectedFile);
			const response = serviceResponse.data;

			if (response.succeeded === true && response.data) {
				setCreatedCount(response.data.created_count);

				if (response.data.result === true) {
					setSelectedFile(null);
					fileInputRef.current.value = '';
					showMessage(true, MessageBarType.success, `${t('data-lang-msg-user-creationsuccess')} (${response.data.created_count})`);
				}
				else {
					if (response.data.l_result_validation && response.data.l_result_validation.length > 0) {
						setProcessedFile(response.data.l_result_validation[0]);
						showMessage(true, MessageBarType.error, t('data-lang-msg-templateerror'));
					}
					else {
						showMessage(true, MessageBarType.error, t('data-lang-msg-operationerror'));
					}
				}
			}
			else {
				showMessage(true, MessageBarType.error, t(response.message ? response.message : 'data-lang-msg-operationerror'));
			}
		}
		catch (error) {
			const responseMessage = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'data-lang-msg-operationerror';
			showMessage(true, MessageBarType.error, t(responseMessage));
		}
		finally {
			setUploadInProgress(false);
		}
	};

	const handlerUploadFile = async () => {
		const clientId = getClientId();

		if (clientId === null) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-selectclient'));
			return;
		}

		if (selectedFile === null) {
			showMessage(true, MessageBarType.error, t('data-lang-msg-selectfile'));
			return;
		}

		setUploadInProgress(true);
		setProcessedFile(null);
		setCreatedCount(null);

		try {
			const serviceResponse = await BulkUserService.postBulkUsers(clientId, selectedFile);
			const response = serviceResponse.data;

			if (response.succeeded === true && response.data) {
				setCreatedCount(response.data.created_count);

				if (response.data.result === true) {
					setSelectedFile(null);
					fileInputRef.current.value = '';
					showMessage(true, MessageBarType.success, `${t('data-lang-msg-user-creationsuccess')} (${response.data.created_count})`);
				}
				else {
					if (response.data.l_result_validation && response.data.l_result_validation.length > 0) {
						setProcessedFile(response.data.l_result_validation[0]);
						showMessage(true, MessageBarType.error, t('data-lang-msg-templateerror'));
					}
					else {
						showMessage(true, MessageBarType.error, t('data-lang-msg-operationerror'));
					}
				}
			}
			else {
				showMessage(true, MessageBarType.error, t(response.message ? response.message : 'data-lang-msg-operationerror'));
			}
		}
		catch (error) {
			const responseMessage = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'data-lang-msg-operationerror';
			showMessage(true, MessageBarType.error, t(responseMessage));
		}
		finally {
			setUploadInProgress(false);
		}
	};

	const downloadErrorFile = () => {
		downloadBase64File(processedFile, `Plantilla Usuarios ${getFileDate()} Processed File.xlsx`);
	};

	return (
		<>
			<AuthVerify currentToken={currentAuth} />
			<Row>
				<Col sm={12} md={12} lg={12} xl={12}>
					{messageConfig.showMessage &&
					<MessageBar
						dismissButtonAriaLabel="Close"
						styles={{root:{marginTop: "-96px !important"}}}
						messageBarType={messageConfig.messageType}
						onDismiss={() => showMessage(false, messageConfig.messageType, '')}>
						{messageConfig.message}
						{processedFile !== null &&
							<>
								&nbsp;
								<button type="button" className="btn btn-link p-0 align-baseline" onClick={downloadErrorFile}>
									{t('data-lang-msg-bulk-download-errors')}
								</button>
							</>
						}
					</MessageBar>
					}
				</Col>
				<Col sm={12} md={12} lg={12} xl={12}>
					<Card className="rounded-3 border card">
						<Card.Header>
							<Card.Title>
								<h4><b>{t('data-lang-pagename-bulkcreateuser')}</b></h4>
							</Card.Title>
						</Card.Header>
						<Card.Body className="col-xs-12">
							<Row>
								{userClientId === null &&
									<Col xs={12} sm={6} md={6} lg={4} xl={4}>
										<Form.Label>{t('data-lang-lbl-client')}</Form.Label>
										{clientList && (
											<Form.Select
												className="form-control"
												onChange={onChangeClient}
												value={selectedClientId ?? '0'}
											>
												{clientList.map((client, index) => <option key={index} value={client.id}>{client.name}</option>)}
											</Form.Select>
										)}
									</Col>
								}
							</Row>
							<Row className="pt-3">
								<Col xs={12}>
									<div className="d-none">
										<input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileInput} />
									</div>
									<div className="d-flex">
										<div className="px-1">
											<ActionButton
												id="btnGetTemplate"
												iconProps={{ iconName: "Download" }}
												text={t('data-lang-btn-bulk-gettemplate')}
												disabled={uploadInProgress}
												onClick={handlerExportTemplate} />
										</div>
										<div className="px-1">
											<ActionButton
												id="btnSelectFile"
												styles={{ root: { color: (selectedFile !== null ? "green" : undefined) } }}
												iconProps={{ iconName: (selectedFile !== null ? "SkypeCircleCheck" : "Attach") }}
												text={selectedFile !== null ? selectedFile.name : t('data-lang-btn-bulk-attachtemplate')}
												disabled={uploadInProgress}
												onClick={handlerSelectFile} />
										</div>
										<div className="px-1">
											<ActionButton
												id="btnUploadFile"
												iconProps={{ iconName: "Upload" }}
												text={t('data-lang-btn-bulk-uploadandprocesstemplate')}
												disabled={uploadInProgress}
												onClick={handlerUploadFile} />
										</div>
										<div className="px-1">
											<ActionButton
												id="btnUploadEmailFile"
												iconProps={{ iconName: "Upload" }}
												text={t('data-lang-btn-bulk-uploadandprocessemailtemplate')}
												disabled={uploadInProgress}
												onClick={handlerUploadEmailFile} />
										</div>
										{uploadInProgress &&
											<div className="px-1 my-auto">
												<Spinner size={SpinnerSize.medium} />
											</div>
										}
									</div>
								</Col>
							</Row>
							{createdCount !== null && createdCount > 0 &&
								<Row className="pt-2">
									<Col xs={12}>
										<span>{t('data-lang-msg-bulk-created-count')}: <b>{createdCount}</b></span>
									</Col>
								</Row>
							}
						</Card.Body>
						<Card.Footer>
						</Card.Footer>
					</Card>
				</Col>
			</Row>
			<Row className="d-md-flex justify-content-md-end">
				<Col xs={12} sm={6} md={4} lg={3} xl={3} xxl={2}>
					<Link to="/admin" onClick={(e) => uploadInProgress && e.preventDefault()}>
						<button data-component-id="btnGoHome" type="button" className="btn btn-primary col-12 mt-2" disabled={uploadInProgress}>{t('data-lang-btn-gohome')}</button>
					</Link>
				</Col>
			</Row>
		</>
	);
};

export default BulkCreateUser;
